import {
	DeletedHubItemStatisticModel,
	getHubStatistics,
	HubItemStatisticKind,
	HubItemStatisticModel,
	HubItemStatisticsStatus,
	HubItemStatisticSummaryModel
} from "client/Api/Analytics/analyticsStatistics";
import { Dialog, NoResults } from "client/Components";
import {
	AlertManager,
	useAlerts,
	useForceRerender,
	useOpenDialog
} from "client/Hooks";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { AnalyticsDetailedList } from "./AnalyticsDetailedList";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { HubThumbnail } from "client/Features/Hub/HubThumbnail/HubThumbnail";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import styles from "./HubUsageDialog.module.less";
import { errorMessageKeyPerHubStatisticsKind, hubItemKindPerStatisticsKind } from "./common";
import { never } from "client_legacy/ndaw/errors";
import { useOpenHubItem } from "./useOpenHubItem";

export const MAX_NUMBER_OF_ITEMS_TO_SHOW_IN_DIALOG = 50;

export type HubUsageDialogProps = {
	kind: HubItemStatisticKind;
	selectedDaysRange: number;
	onClose: () => void;
};

export const HubUsageDialog = (props: HubUsageDialogProps): ReactElement => {
	const {
		kind,
		selectedDaysRange,
		onClose
	} = props;

	const [state, setState] = useState<State>({ status: "loading" });

	const alertManager: AlertManager = useAlerts();
	const [hubItemDetails, openHubItemDetails] = useOpenHubItem();

	const [reload, dataVersion] = useForceRerender();

	useEffect(
		(): EffectWithDestructorResult => {
			hasImplicitDependencyOn(dataVersion);

			async function fetchData(signal: AbortSignal): Promise<void> {
				setState({status: "loading"});

				try {
					const statistics: HubItemStatisticSummaryModel = await getHubStatistics(
						selectedDaysRange,
						kind,
						MAX_NUMBER_OF_ITEMS_TO_SHOW_IN_DIALOG,
						signal
					);

					setState({
						status: "ready",
						statistics
					});
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					setState({ status: "error" });

					alertManager.showError(
						localize("_GeneralErrorMessage")
					);
				}
			}

			const cancellation = new AbortController();

			fetchData(cancellation.signal);

			return () => cancellation.abort();
		},
		[
			alertManager,
			dataVersion,
			kind,
			selectedDaysRange
		]
	);

	const hubItemKind: HubItemKind = hubItemKindPerStatisticsKind[kind];

	const openHubItemDetailsHandler = useCallback(
		(model: Exclude<HubItemStatisticModel, DeletedHubItemStatisticModel>): void => {
			if (model.status === HubItemStatisticsStatus.NoAccess) {
				alertManager.showError(localize("_YouDontHavePermissionsToViewThisItem"));
				return;
			}

			openHubItemDetails(
				model.status,
				hubItemKind,
				model.hubItemId
			);
		},
		[
			alertManager,
			openHubItemDetails,
			hubItemKind
		]
	);

	const renderContent = (): ReactElement | null => {
		switch (state.status) {
			case "loading":
				return null;
			case "error":
				return (
					<NoResults
						icon="chart-line"
						title={localize("_UnableToRetrieveData")}
						message={localize(errorMessageKeyPerHubStatisticsKind[kind])}
						height="fill"
						onRetry={reload}
					/>
				);
			case "ready":
				return (
					<AnalyticsDetailedList>
						{state.statistics
							.statistics
							.map((x, i) => {
								return (
									<HubUsageStatisticItem
										key={i}
										statistic={x}
										hubItemKind={hubItemKind}
										onClick={openHubItemDetailsHandler}
									/>
								);
							})
						}
					</AnalyticsDetailedList>
				);
			default:
				never(state);
		}
	};

	return (
		<>
			<Dialog
				hasBusyIndicator={state.status === "loading"}
				backdrop={hubItemDetails ? "invisible" : "visible"}
			>
				<Dialog.Header
					title={`${localize(hubItemKeyPerKind[hubItemKind])} - ${localize(titleKeyPerKind[kind])}`}
					onClose={onClose}
				/>
				<Dialog.Body goesEdgeToEdge>
					{renderContent()}
				</Dialog.Body>
				<Dialog.Footer />
			</Dialog>
			{hubItemDetails}
		</>
	);
};
HubUsageDialog.displayName = "HubUsageDialog";

type HubUsageStatisticItemProps = {
	statistic: HubItemStatisticModel;
	hubItemKind: HubItemKind;
	onClick: (model: Exclude<HubItemStatisticModel, DeletedHubItemStatisticModel>) => void;
};

const HubUsageStatisticItem = (props: HubUsageStatisticItemProps): ReactElement => {
	const {
		statistic,
		hubItemKind,
		onClick
	} = props;

	const counter: number = statistic.value;

	const title: string = useMemo(
		(): string => getHubItemStatisticTitle(statistic),
		[statistic]
	);

	const onClickHandler = useCallback(
		(): void => {
			if (statistic.status === HubItemStatisticsStatus.Deleted) {
				throw new Error("Deleted item cannot be clicked");
			}

			onClick(statistic);
		},
		[onClick, statistic]
	);

	const renderThumbnail = (): ReactElement => {
		return (
			<div className={styles.thumbnailContainer}>
				<HubThumbnail
					hubItemKind={hubItemKind}
					title={title}
					media={statistic.hubItemMedia}
					mediaPlaceholderSize="extra-small"
				/>
			</div>
		);
	};

	switch (statistic.status) {
		case HubItemStatisticsStatus.Published:
		case HubItemStatisticsStatus.NoAccess:
			return (
				<AnalyticsDetailedList.Item
					thumbnail={renderThumbnail()}
					title={title}
					counter={counter}
					onClick={onClickHandler}
				/>
			);
		case HubItemStatisticsStatus.NotPublished:
			return (
				<AnalyticsDetailedList.Item
					thumbnail={renderThumbnail()}
					title={title}
					counter={counter}
					subtitle={localize("_Archived")}
					isDisabled
					onClick={onClickHandler}
				/>
			);
		case HubItemStatisticsStatus.Deleted:
			return (
				<AnalyticsDetailedList.Item
					thumbnail={renderThumbnail()}
					title={title}
					counter={counter}
					isDisabled
				/>
			);
		default:
			never(statistic);
	}
};
HubUsageStatisticItem.displayName = "HubUsageStatisticItem";

export type HubUsageDialogData = Omit<HubUsageDialogProps, "onClose">;

export function useHubUsageDialog(): [
	element: ReactElement | null,
	open: (openData: HubUsageDialogData) => Promise<void>
] {
	return useOpenDialog<HubUsageDialogData>(
		(openData, onClose) => (
			<HubUsageDialog
				{...openData}
				onClose={onClose}
			/>
		)
	);
}

export const getHubItemStatisticTitle = (model: HubItemStatisticModel): string => {
	switch (model.status) {
		case HubItemStatisticsStatus.Deleted:
			return localize("_DeletedItem");
		case HubItemStatisticsStatus.NoAccess:
			return localize("_NoAccess");
		case HubItemStatisticsStatus.NotPublished:
		case HubItemStatisticsStatus.Published:
			return model.hubItemTitle;
		default:
			never(model);
	}
};

export const hubItemKeyPerKind: { [key in HubItemKind]: LocalizationKey; } = {
	[HubItemKind.Announcement]: "_Announcements",
	[HubItemKind.Event]: "_Events",
	[HubItemKind.KnowledgeItem]: "_KnowledgeArticles"
};

const titleKeyPerKind: { [key in HubItemStatisticKind]: LocalizationKey } = {
	[HubItemStatisticKind.AnnouncementComments]: "_MostComments",
	[HubItemStatisticKind.AnnouncementReactions]: "_MostReactions",
	[HubItemStatisticKind.AnnouncementUniqueViews]: "_MostUniqueViews",
	[HubItemStatisticKind.EventAttendance]: "_MostSignUps",
	[HubItemStatisticKind.EventUniqueViews]: "_MostUniqueViews",
	[HubItemStatisticKind.EventComments]: "_MostComments",
	[HubItemStatisticKind.EventReactions]: "_MostReactions",
	[HubItemStatisticKind.KnowledgeItemTotalViews]: "_MostTotalViews",
	[HubItemStatisticKind.KnowledgeItemUniqueViews]: "_MostUniqueViews",
	[HubItemStatisticKind.KnowledgeItemComments]: "_MostComments",
	[HubItemStatisticKind.KnowledgeItemReactions]: "_MostReactions",
};

type State = {
	status: "loading";
} | {
	status: "error";
} | {
	status: "ready";
	statistics: HubItemStatisticSummaryModel;
};
